import {
  EXPLANATION_NUMBER_KEY,
  EXPLANATION_RULE_KEY,
  EXPLANATION_TIME_PER_DAYS_KEY,
  OT_TYPE_KEY,
  REFUND_HOURS_PER_EXPLANATION_KEY,
  URL_POLICY_KEY,
  WORK_SCHEDULE_CODE_KEY,
  WORK_SCHEDULE_TYPE_KEY,
} from "@/pages/PolicyManagement/constants";
import {
  HISTORY_POLICY_MANAGEMENT_EMPLOYEE_LIST,
  POLICY_MANAGEMENT_EMPLOYEE_HISTORY_INFO,
  POLICY_MANAGEMENT_EMPLOYEE_HISTORY_LIST,
  POLICY_MANAGEMENT_EMPLOYEE_LIST,
  SET_PAGES,
} from "@/store/mutation-types";
import {
  convertObjectToParams,
  DATE_FORMAT_V2_DMY_HM,
  downloadFile,
  formatDateStringToString,
  formatExplanationRule,
  getInfoStaff,
  getInfoStaffFull,
  PAGE_DEFAULT,
} from "@/utils";
import http from "../helpers/httpInterceptor";

const state = () => ({
  policyManagementEmployeeList: [],
  policyManagementEmployeeHistoryList: [],
  policyManagementEmployeeHistoryInfo: null,
  historyPolicyManagementEmployeeList: [],
  pages: PAGE_DEFAULT,
});

// mutations
const mutations = {
  [POLICY_MANAGEMENT_EMPLOYEE_LIST](state, data) {
    state.policyManagementEmployeeList = formatData(data);
  },
  [POLICY_MANAGEMENT_EMPLOYEE_HISTORY_LIST](state, data) {
    state.policyManagementEmployeeHistoryList = formatData(data);
  },
  [POLICY_MANAGEMENT_EMPLOYEE_HISTORY_INFO](state, data) {
    state.policyManagementEmployeeHistoryInfo = data;
  },
  [SET_PAGES](state, data) {
    state.pages = data;
  },
  [HISTORY_POLICY_MANAGEMENT_EMPLOYEE_LIST](state, data) {
    state.historyPolicyManagementEmployeeList = formatHistory(data);
  },
};

// actions
const actions = {
  getPolicyManagementEmployeeList({ commit }, payload) {
    return http
      .get(`/tms/job-info-history${convertObjectToParams(payload)}`)
      .then((res) => {
        const {
          data,
          page: { page, size, total_element },
        } = res.data;
        commit(POLICY_MANAGEMENT_EMPLOYEE_LIST, data ?? []);
        commit(SET_PAGES, {
          page,
          size,
          total: total_element,
        });
        return res.data;
      })
      .catch((error) => {
        commit(POLICY_MANAGEMENT_EMPLOYEE_LIST, []);
        commit(SET_PAGES, PAGE_DEFAULT);
        throw error;
      });
  },
  getPolicyManagementListByEmployee({ commit }, payload) {
    return http
      .get(`/tms/job-info-history/employee${convertObjectToParams(payload)}`)
      .then((res) => {
        const {
          data,
          page: { page, size, total_element, advance_object },
        } = res.data;
        commit(POLICY_MANAGEMENT_EMPLOYEE_HISTORY_LIST, data ?? []);
        commit(POLICY_MANAGEMENT_EMPLOYEE_HISTORY_INFO, advance_object);
        commit(SET_PAGES, {
          page,
          size,
          total: total_element,
        });
        return res.data;
      })
      .catch((error) => {
        commit(POLICY_MANAGEMENT_EMPLOYEE_HISTORY_LIST, []);
        commit(SET_PAGES, PAGE_DEFAULT);
        throw error;
      });
  },
  createPolicyManagementEmployee(_, payload) {
    return http
      .post(`/tms/job-info-history/set-policy`, payload)
      .then((res) => {
        return res.data;
      })
      .catch((error) => {
        throw error;
      });
  },
  editPolicyManagementEmployee(_, payload) {
    return http
      .put(`/tms/job-info-history/edit-policy`, payload)
      .then((res) => {
        return res.data;
      })
      .catch((error) => {
        throw error;
      });
  },
  deletePolicyManagementEmployee(_, payload) {
    const { id } = payload;
    return http
      .delete(`/tms/job-info-history/delete-policy/${id}`)
      .then((res) => {
        return res.data;
      })
      .catch((error) => {
        throw error;
      });
  },
  getHistoryPolicyManagementEmployee({ commit }, payload) {
    const { id } = payload;
    return http
      .get(`/tms/job-info-history/get-history/${id}`)
      .then((res) => {
        commit(HISTORY_POLICY_MANAGEMENT_EMPLOYEE_LIST, res?.data?.data ?? []);
        return res.data;
      })
      .catch((error) => {
        commit(HISTORY_POLICY_MANAGEMENT_EMPLOYEE_LIST, []);
        throw error;
      });
  },
  importPolicyManagementEmployee(_, payload) {
    const formData = new FormData();
    formData.append("files", payload[0]);
    return http
      .post(`/tms/job-info-history/import`, formData, { responseType: "blob" })
      .then((res) => {
        const { filename, code } = res.headers;
        if (code != 200) {
          downloadFile(res.data, filename ?? "LeaveCarryover.xlsx", res.headers["content-type"]);
        }
        return res;
      })
      .catch((error) => {
        throw error;
      });
  },
  downloadTemplate() {
    return http
      .get(`/tms/job-info-history/download-template`, { responseType: "blob" })
      .then((res) => {
        const { filename } = res.headers;
        downloadFile(res.data, filename ?? "Template.xlsx", res.headers["content-type"]);
        return res;
      })
      .catch((error) => {
        throw error;
      });
  },
};

const formatData = (data) => {
  if (!data || data?.length === 0) {
    return [];
  }
  return data.map((item) => {
    const { staff, apply_date, expire_date } = item;
    return {
      ...item,
      fullName: getInfoStaffFull(staff),
      applyDateFormat: formatDateStringToString(apply_date),
      expireDateFormat: formatDateStringToString(expire_date),
    };
  });
};

export const formatHistory = (data) => {
  if (!data || data.length <= 0) return [];
  return data?.map((item) => {
    const { new_record, createdDate, action, employee, created_by } = item;
    const {
      apply_date,
      expire_date,
      rule_type,
      work_schedule_code,
      refund_hours_per_explanation,
      explanation_number,
      explanation_time_per_days,
      ot_type,
      url_policy,
      modified_date,
      created_date,
    } = new_record;
    return {
      ...item,
      createdDateFormat: formatDateStringToString(createdDate, DATE_FORMAT_V2_DMY_HM),
      new_record: {
        ...new_record,
        createdByName: getInfoStaff(created_by),
        createdDateFormat: formatDateStringToString(created_date, DATE_FORMAT_V2_DMY_HM),
        updatedBy: getInfoStaff(created_by),
        modifiedDateFormat: formatDateStringToString(modified_date, DATE_FORMAT_V2_DMY_HM),
        fullName: getInfoStaff(employee),
        applyDateFormat: formatDateStringToString(apply_date),
        expireDateFormat: formatDateStringToString(expire_date),
        [WORK_SCHEDULE_TYPE_KEY]: rule_type,
        [WORK_SCHEDULE_CODE_KEY]: work_schedule_code,
        [EXPLANATION_TIME_PER_DAYS_KEY]: explanation_time_per_days,
        [REFUND_HOURS_PER_EXPLANATION_KEY]: refund_hours_per_explanation,
        [EXPLANATION_NUMBER_KEY]: explanation_number,
        [EXPLANATION_RULE_KEY]: formatExplanationRule(new_record),
        [OT_TYPE_KEY]: ot_type,
        [URL_POLICY_KEY]: url_policy,
        action,
      },
    };
  });
};
export default {
  namespaced: true,
  state,
  actions,
  mutations,
};
